"use strict";
/*
 * Pure analysis helpers for mouse-movement bot detection.
 *
 * A mouse path is a list of points we sampled over time. Real people move the
 * mouse in a messy way: the speed keeps changing, the path curves, it jitters,
 * and it spreads across the screen. Bots that fake mouse movement tend to move
 * at one constant speed, in straight lines, along a single narrow axis. These
 * helpers measure those traits.
 *
 * Units: positions are in pixels (px), time is in milliseconds (ms), and angles
 * are in radians. All thresholds are rough guesses, meant to be tuned with real
 * traffic.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.analyzeMouseBehavior = exports.hasLowSpatialEntropy = exports.hasNoCurvature = exports.hasConstantVelocity = exports.getTotalPathLengthPx = exports.MIN_POINTS_FOR_ANALYSIS = void 0;
const helpers_1 = require("lib/bots/helpers");
// Fewest points we need before the speed/curve/spread checks make sense.
exports.MIN_POINTS_FOR_ANALYSIS = 12;
// If the whole path is shorter than this many pixels, the movement is too small
// to judge.
const MIN_PATH_LENGTH_PX = 50;
// Steps shorter than this many pixels are treated as noise and ignored.
const MIN_STEP_LENGTH_PX = 1;
// Speed variation (a unitless ratio) below this looks unnaturally constant.
const CONSTANT_SPEED_RATIO_THRESHOLD = 0.08;
// Average turn (in radians) below this means the path is basically straight.
const STRAIGHT_PATH_TURN_RADIANS = 0.05;
// Narrowness ratio (unitless) below this means movement stays on one axis.
const NARROW_AXIS_RATIO_THRESHOLD = 0.02;
const distanceBetweenPx = (pointA, pointB) => {
    return Math.hypot(pointB.x - pointA.x, pointB.y - pointA.y);
};
// The turn (in radians) from one direction to another, folded into 0..PI so a
// left turn and a right turn of the same size count the same.
const turnBetweenRadians = (firstDirectionRadians, secondDirectionRadians) => {
    let turnRadians = secondDirectionRadians - firstDirectionRadians;
    while (turnRadians > Math.PI) {
        turnRadians -= 2 * Math.PI;
    }
    while (turnRadians < -Math.PI) {
        turnRadians += 2 * Math.PI;
    }
    return Math.abs(turnRadians);
};
// Turns the raw points into steps, dropping tiny duplicate / sub-pixel moves
// that would otherwise pollute the speed and direction stats.
const getMeaningfulSteps = (points) => {
    const steps = [];
    for (let index = 1; index < points.length; index += 1) {
        const from = points[index - 1];
        const to = points[index];
        const lengthPx = distanceBetweenPx(from, to);
        if (lengthPx < MIN_STEP_LENGTH_PX) {
            continue;
        }
        steps.push({
            from,
            to,
            lengthPx,
            durationMs: Math.max(to.t - from.t, 1),
        });
    }
    return steps;
};
// Total length of the mouse path, in pixels.
const getTotalPathLengthPx = (points) => {
    return getMeaningfulSteps(points).reduce((totalPx, step) => totalPx + step.lengthPx, 0);
};
exports.getTotalPathLengthPx = getTotalPathLengthPx;
// Constant speed: the speed barely changes along the whole path.
const hasConstantVelocity = (points) => {
    const steps = getMeaningfulSteps(points);
    if (steps.length < 3) {
        return false;
    }
    const speedsPxPerMs = steps.map((step) => step.lengthPx / step.durationMs);
    const averageSpeedPxPerMs = (0, helpers_1.calculateAverage)(speedsPxPerMs);
    if (averageSpeedPxPerMs === 0) {
        return false;
    }
    return (0, helpers_1.coefficientOfVariation)(speedsPxPerMs) < CONSTANT_SPEED_RATIO_THRESHOLD;
};
exports.hasConstantVelocity = hasConstantVelocity;
// No curve: the direction barely changes from step to step (a straight line).
const hasNoCurvature = (points) => {
    const steps = getMeaningfulSteps(points);
    if (steps.length < 3) {
        return false;
    }
    const directionsRadians = steps.map((step) => {
        return Math.atan2(step.to.y - step.from.y, step.to.x - step.from.x);
    });
    const turnsRadians = [];
    for (let index = 1; index < directionsRadians.length; index += 1) {
        turnsRadians.push(turnBetweenRadians(directionsRadians[index - 1], directionsRadians[index]));
    }
    return (0, helpers_1.calculateAverage)(turnsRadians) < STRAIGHT_PATH_TURN_RADIANS;
};
exports.hasNoCurvature = hasNoCurvature;
// Narrow axis: the points spread out in one direction but almost not at all in
// the perpendicular one (the cloud of points collapses onto a line).
const hasLowSpatialEntropy = (points) => {
    if (points.length < exports.MIN_POINTS_FOR_ANALYSIS) {
        return false;
    }
    const averageXPx = (0, helpers_1.calculateAverage)(points.map((point) => point.x));
    const averageYPx = (0, helpers_1.calculateAverage)(points.map((point) => point.y));
    // Spread values are in px^2 (how far points sit from the average, squared).
    let spreadXX = 0;
    let spreadYY = 0;
    let spreadXY = 0;
    for (const point of points) {
        const offsetXPx = point.x - averageXPx;
        const offsetYPx = point.y - averageYPx;
        spreadXX += offsetXPx * offsetXPx;
        spreadYY += offsetYPx * offsetYPx;
        spreadXY += offsetXPx * offsetYPx;
    }
    spreadXX /= points.length;
    spreadYY /= points.length;
    spreadXY /= points.length;
    // Eigenvalues of the 2x2 spread (covariance) matrix: the spread along the main
    // axis (wide) and along the perpendicular axis (narrow), both in px^2.
    const trace = spreadXX + spreadYY;
    const determinant = spreadXX * spreadYY - spreadXY * spreadXY;
    const halfAxisGap = Math.sqrt(Math.max(Math.pow((trace / 2), 2) - determinant, 0));
    const wideAxisSpread = trace / 2 + halfAxisGap;
    const narrowAxisSpread = Math.max(trace / 2 - halfAxisGap, 0);
    if (wideAxisSpread <= 0) {
        return false; // no spread at all => handled by the no-movement check
    }
    return narrowAxisSpread / wideAxisSpread < NARROW_AXIS_RATIO_THRESHOLD;
};
exports.hasLowSpatialEntropy = hasLowSpatialEntropy;
// True if the mouse path shows any bot-like trait.
const analyzeMouseBehavior = (points) => {
    if (points.length < exports.MIN_POINTS_FOR_ANALYSIS) {
        return false;
    }
    if ((0, exports.getTotalPathLengthPx)(points) < MIN_PATH_LENGTH_PX) {
        return false;
    }
    return (0, exports.hasConstantVelocity)(points) || (0, exports.hasNoCurvature)(points) || (0, exports.hasLowSpatialEntropy)(points);
};
exports.analyzeMouseBehavior = analyzeMouseBehavior;
